// Homepage-aligned palette — zinc grays, brand blues, #4D4D4D, red for negatives
export const PASTEL_PALETTE = {
  water:       { bg: '#eef1ff', text: '#2f29c7' },  // Success / active   (brand-50 / brand-700)
  lavender:    { bg: '#e0e4ff', text: '#3832e3' },  // Info / in-progress (brand-100 / brand-600)
  lotion:      { bg: '#f4f4f5', text: '#52525b' },  // Near-neutral       (zinc-100 / zinc-600)
  goldenrod:   { bg: '#e4e4e7', text: '#3f3f46' },  // Pending / waiting  (zinc-200 / zinc-700)
  flesh:       { bg: '#d4d4d8', text: '#27272a' },  // Warning / attention(zinc-300 / zinc-800)
  cottonCandy: { bg: '#fee2e2', text: '#991b1b' },  // Negative / failed  (red-100 / red-800)
};

const p = PASTEL_PALETTE;

// ─── Status → palette entry ────────────────────────────────────────────
const STATUS_MAP = {
  // Success / active
  active:               p.water,
  completed:            p.water,
  verified:             p.water,
  approved:             p.water,
  healthy:              p.water,
  confirmed:            p.water,
  cleared:              p.water,
  resolved:             p.water,
  current:              p.water,
  paid_off:             p.water,
  accepted:             p.water,
  fully_subscribed:     p.water,
  matured:              p.water,

  // Info / in-progress
  open:                 p.lavender,
  evaluating:           p.lavender,
  disbursed:            p.lavender,
  trialing:             p.lavender,
  investigating:        p.lavender,
  partially_subscribed: p.lavender,
  submitted:            p.lavender,
  review:               p.lavender,

  // Near-neutral
  draft:                p.lotion,
  email_sent:           p.lotion,
  incomplete:           p.lotion,

  // Pending / waiting
  pending:              p.goldenrod,
  processing:           p.goldenrod,
  pending_settlement:   p.goldenrod,
  pending_kyc:          p.goldenrod,
  pending_aml:          p.goldenrod,
  pending_approval:     p.goldenrod,

  // Warning / attention
  expiring:             p.flesh,
  maturing:             p.flesh,
  overdue:              p.flesh,
  escalated:            p.flesh,
  degraded:             p.flesh,

  // Negative / failed (red)
  denied:               p.cottonCandy,
  suspended:            p.cottonCandy,
  rejected:             p.cottonCandy,
  failed:               p.cottonCandy,
  defaulted:            p.cottonCandy,
  expired:              p.cottonCandy,
  cancelled:            p.cottonCandy,
  flagged:              p.cottonCandy,
  unhealthy:            p.cottonCandy,

  // LP / Treasury Dealer statuses
  live:                 p.lavender,
  amended:              p.lavender,
  quoted:               p.lavender,
  won:                  p.water,
  lost:                 p.cottonCandy,
  declined:             p.cottonCandy,
  booked:               p.goldenrod,
  instructed:           p.goldenrod,
  awaiting_funds:       p.flesh,
  funds_received:       p.water,
  released:             p.water,
  csd_payment_confirmed: p.water,
  review_due:           p.flesh,
  prohibited:           p.cottonCandy,
  withdrawn:            p.lotion,

  // Dealing-specific statuses
  allocated:            p.water,
  payment_due:          p.goldenrod,
  paid:                 p.water,
  submitted_to_csd:     p.lavender,
  partially_allocated:  p.flesh,
  unsuccessful:         p.cottonCandy,
  payment_sent:         p.lavender,
  arrived:              p.water,
  fx_conversion:        p.goldenrod,
  auto_roll:            p.lavender,
  repatriate:           p.flesh,
  results_published:    p.water,

  // Transaction lifecycle states
  kyc_maker_review:           p.lavender,
  kyc_maker_approved:         p.water,
  kyc_checker_review:         p.lavender,
  kyc_checker_approved:       p.water,
  cds_card_pending:           p.goldenrod,
  cds_card_checker_signed:    p.lavender,
  cds_card_maker_signed:      p.lavender,
  cds_card_complete:          p.water,
  auction_eligible:           p.lotion,
  bid_placed:                 p.lavender,
  bid_allocated:              p.water,
  fx_rfq_sent:                p.goldenrod,
  fx_quotes_received:         p.lavender,
  fx_rate_locked:             p.water,
  fx_rate_expired:            p.cottonCandy,
  bank_instruction_sent:      p.goldenrod,
  bank_instruction_confirmed: p.water,
  bank_instruction_rejected:  p.cottonCandy,
  nostro_funds_received:      p.water,
  nostro_funds_timeout:       p.cottonCandy,
  csd_confirmed:              p.water,
  csd_rejected:               p.cottonCandy,
  portfolio_active:           p.water,
  maturity_notice_sent:       p.flesh,
  rollover_elected:           p.lavender,
  realise_elected:            p.lavender,
  proceeds_repatriated:       p.water,
  settlement_complete:        p.water,
  removed_from_portfolio:     p.lotion,
};

// ─── Severity ──────────────────────────────────────────────────────────
const SEVERITY_MAP = {
  low:      p.water,
  medium:   p.goldenrod,
  high:     p.flesh,
  critical: p.cottonCandy,
};

// ─── Risk ──────────────────────────────────────────────────────────────
const RISK_MAP = {
  low:      p.water,
  moderate: p.goldenrod,
  high:     p.flesh,
  critical: p.cottonCandy,
};

// ─── Role ──────────────────────────────────────────────────────────────
const ROLE_MAP = {
  admin:              p.lavender,
  super_admin:        p.lavender,
  analyst:            p.water,
  viewer:             p.lotion,
  dealer:             p.goldenrod,
  compliance_maker:   p.flesh,
  compliance_checker: p.goldenrod,
  treasury_dealer:    p.lavender,
  forwards_dealer:    p.water,
  family_office:      p.water,
  individual:         p.lotion,
};

// ─── Tier ──────────────────────────────────────────────────────────────
const TIER_MAP = {
  free:       p.lotion,
  basic:      p.water,
  standard:   p.lavender,
  premium:    p.goldenrod,
  enterprise: p.flesh,
};

// ─── Class / org type ──────────────────────────────────────────────────
const CLASS_MAP = {
  bank:       p.lavender,
  investor:   p.water,
  reseller:   p.goldenrod,
  individual: p.flesh,
  platform:   p.lotion,
};

// ─── Icon color names (used by StatCard components) ────────────────────
const ICON_MAP = {
  green:  p.water,
  blue:   p.lavender,
  red:    p.cottonCandy,
  purple: p.flesh,
  amber:  p.goldenrod,
  yellow: p.goldenrod,
  indigo: p.lavender,
  rose:   p.cottonCandy,
  pink:   p.cottonCandy,
};

// ─── Trend direction ───────────────────────────────────────────────────
const TREND_MAP = {
  up:   p.water,
  down: p.cottonCandy,
};

// ─── Chart colors (homepage palette only) ──────────────────────────────
export const CHART = {
  water:       { stroke: '#3832e3', fill: '#3832e3' },  // Brand-600
  lavender:    { stroke: '#7b7eff', fill: '#7b7eff' },  // Brand-400
  lotion:      { stroke: '#e4e4e7', fill: '#e4e4e7' },  // zinc-200
  goldenrod:   { stroke: '#a1a1aa', fill: '#a1a1aa' },  // zinc-400
  flesh:       { stroke: '#d4d4d8', fill: '#d4d4d8' },  // zinc-300
  cottonCandy: { stroke: '#52525b', fill: '#52525b' },  // zinc-600
};

// Ordered array for pie/bar series — visually distinct rotation
export const CHART_SERIES = [
  '#3832e3',  // Brand-600 (blue)
  '#4D4D4D',  // Primary accent (dark gray)
  '#7b7eff',  // Brand-400 (medium blue)
  '#a1a1aa',  // zinc-400 (medium gray)
  '#c6ccff',  // Brand-200 (light lilac)
  '#d4d4d8',  // zinc-300 (light gray)
];

// ─── Helper: map entry → inline style object ───────────────────────────
const toStyle = (entry) =>
  entry
    ? { backgroundColor: entry.bg, color: entry.text }
    : { backgroundColor: p.lotion.bg, color: p.lotion.text };

// ─── Public API ────────────────────────────────────────────────────────
export const getStatusStyle   = (s) => toStyle(STATUS_MAP[s?.toLowerCase?.()]);
export const getSeverityStyle = (s) => toStyle(SEVERITY_MAP[s?.toLowerCase?.()]);
export const getRiskStyle     = (s) => toStyle(RISK_MAP[s?.toLowerCase?.()]);
export const getRoleStyle     = (s) => toStyle(ROLE_MAP[s?.toLowerCase?.()]);
export const getTierStyle     = (s) => toStyle(TIER_MAP[s?.toLowerCase?.()]);
export const getClassStyle    = (s) => toStyle(CLASS_MAP[s?.toLowerCase?.()]);
export const getIconStyle     = (s) => toStyle(ICON_MAP[s?.toLowerCase?.()]);
export const getTrendStyle    = (positive) => toStyle(positive ? TREND_MAP.up : TREND_MAP.down);
